import {
  Document as FileText,
  CheckmarkOutline as CheckCircle,
  Link as LinkIcon,
  Globe,
  MisuseOutline as Ban,
  WarningAlt as AlertTriangle,
  Security as Shield,
  Scale,
  Renew as RefreshCw,
  Email as Mail,
} from '@carbon/icons-react'
const LAST_UPDATED = 'February 28, 2026'
export function TermsSections() {
  return (
    <>
      {/* 1. Interpretation & Definitions */}
      <section id="interpretation" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <FileText className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Interpretation and Definitions
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                Explains key terms used throughout this document so everyone
                understands the same meaning.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>
            The words with capital first letters have defined meanings. These
            meanings apply whether used in singular or plural form.
          </p>
          <p className="font-medium mt-4 text-foreground">Definitions</p>
          <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
            <li>
              <strong>Affiliate</strong>: entity that controls, is controlled
              by, or is under common control with us (≥50% ownership).
            </li>
            <li>
              <strong>Country</strong>: Maharashtra, India.
            </li>
            <li>
              <strong>Company</strong> ("We", "Us", "Our"): Euclideum Solutions
              Pvt. Ltd, 203A, Shiv Heights, Ambedkar Chowk, Thane West – 400601.
            </li>
            <li>
              <strong>Device</strong>: any device that can access the Service
              (computer, phone, tablet…).
            </li>
            <li>
              <strong>Service</strong>: the website https://euclideum.com/.
            </li>
            <li>
              <strong>Terms and Conditions</strong> ("Terms"): these terms,
              including referenced documents.
            </li>
            <li>
              <strong>Third-Party Social Media Service</strong>: third-party
              services/content linked or displayed via the Service.
            </li>
            <li>
              <strong>Website</strong>: euclideum.com, accessible from
              https://euclideum.com/.
            </li>
            <li>
              <strong>You</strong>: the individual or entity accessing/using the
              Service.
            </li>
          </ul>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 2. Acknowledgment */}
      <section id="acknowledgment" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <CheckCircle className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Acknowledgment
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                By using the website you agree to these terms. You must be over
                18. These terms work together with our Privacy Policy.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>
            These Terms and Conditions govern your use of the Service and form
            the agreement between you and the Company.
          </p>
          <p>
            Your access to and use of the Service is conditioned on your
            acceptance of and compliance with these Terms. By accessing or using
            the Service you agree to be bound by these Terms. If you disagree
            with any part, you may not access the Service.
          </p>
          <p>
            You represent that you are over the age of 18. The Company does not
            permit those under 18 to use the Service.
          </p>
          <p>
            Your use is also subject to our{' '}
            <a
              href="/privacy"
              className="text-emerald-600 dark:text-primary-active hover:underline"
            >
              Privacy Policy
            </a>
            , which explains how we handle personal information.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 3. Links to Other Websites */}
      <section id="links" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <LinkIcon className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Links to Other Websites
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                Our site may link to third-party websites. We are not
                responsible for their content, privacy practices, or any damage
                from using them.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>
            Our Service may contain links to third-party websites or services
            not owned or controlled by the Company.
          </p>
          <p>
            The Company has no control over, and assumes no responsibility for,
            the content, privacy policies, or practices of any third-party
            websites or services. You acknowledge that the Company shall not be
            responsible or liable for any damage or loss caused by or alleged to
            be caused by use of or reliance on any such content, goods or
            services.
          </p>
          <p>
            We strongly advise you to read the terms and conditions and privacy
            policies of any third-party sites you visit.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 4. Third-Party Social Media Service */}
      <section id="social-media" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <Globe className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Links from a Third-Party Social Media Service
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                If our service shows content from social media platforms
                (Twitter/X, LinkedIn, etc.), we do not control or endorse it and
                are not responsible for it.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>
            The Service may display, include or link to content or services from
            a Third-Party Social Media Service. Such services are not owned or
            controlled by the Company, and we do not endorse them.
          </p>
          <p>
            The Company is not responsible or liable for any damage or loss
            caused by your access to or use of any Third-Party Social Media
            Service. Your use is governed by that service's own terms and
            privacy policy.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 5. Termination */}
      <section id="termination" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <Ban className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Termination
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                We can end your access immediately if you break these terms.
                When that happens, your right to use the service stops right
                away.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>
            We may terminate or suspend your access immediately, without prior
            notice or liability, for any reason, including if you breach these
            Terms.
          </p>
          <p>
            Upon termination, your right to use the Service ceases immediately.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 6. Limitation of Liability */}
      <section id="liability" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <AlertTriangle className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Limitation of Liability
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                Our total responsibility is limited to what you paid us (or $100
                if nothing was paid). We are not liable for indirect losses like
                lost profits or data.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>
            Notwithstanding any damages you might incur, our entire liability
            and your exclusive remedy shall be limited to the amount actually
            paid by you through the Service or 100 USD if nothing was paid.
          </p>
          <p>
            To the maximum extent permitted by law, we and our suppliers shall
            not be liable for any special, incidental, indirect, or
            consequential damages (including loss of profits, data, business
            interruption, personal injury, loss of privacy…), even if advised of
            the possibility.
          </p>
          <p>
            Some jurisdictions do not allow certain limitations in those cases,
            liability is limited to the greatest extent permitted by law.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 7. "AS IS" and "AS AVAILABLE" Disclaimer */}
      <section id="disclaimer" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <Shield className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              "AS IS" and "AS AVAILABLE" Disclaimer
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                The service is provided without any guarantees no promises it
                will be error-free, uninterrupted, secure, or fit for a
                particular purpose.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>
            The Service is provided "AS IS" and "AS AVAILABLE" with all faults
            and defects, without warranty of any kind.
          </p>
          <p>
            To the maximum extent permitted by law, we disclaim all warranties
            express, implied, statutory including merchantability, fitness for a
            particular purpose, title, non-infringement, and warranties from
            course of dealing or usage.
          </p>
          <p>
            We make no representation that the Service will meet your
            requirements, be uninterrupted, error-free, secure, virus-free, or
            that defects will be corrected.
          </p>
          <p>
            Some jurisdictions limit exclusion of warranties in those cases,
            exclusions apply to the greatest extent enforceable.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 8. Governing Law */}
      <section id="governing-law" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <Scale className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Governing Law
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                These terms are governed by Indian law (Maharashtra).
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-3 text-sm text-muted-foreground">
          <p>
            The laws of the Country (Maharashtra, India), excluding conflict of
            law rules, govern these Terms and your use of the Service.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 9. Disputes Resolution */}
      <section id="disputes" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <Scale className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Disputes Resolution
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                Try to resolve issues informally first by contacting us.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-3 text-sm text-muted-foreground">
          <p>
            If you have any concern or dispute, you agree to first try to
            resolve it informally by contacting the Company.
          </p>
          <p>
            <strong>For EU Users:</strong> You benefit from mandatory provisions
            of the law of your country of residence.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 10. Severability and Waiver */}
      <section id="severability" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <RefreshCw className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Severability and Waiver
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                If any part is invalid, the rest remains valid. Not enforcing a
                rule once doesn't mean we waive it forever.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-3 text-sm text-muted-foreground">
          <p>
            <strong>Severability:</strong> If any provision is invalid, it will
            be adjusted to achieve its purpose as much as possible; remaining
            provisions stay in effect.
          </p>
          <p>
            <strong>Waiver:</strong> Failure to enforce a right does not waive
            future enforcement or constitute waiver of subsequent breaches.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 11. Translation Interpretation */}
      <section id="translation" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <Globe className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Translation Interpretation
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                If we provide a translation, the English version is the official
                one in case of disputes.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-3 text-sm text-muted-foreground">
          <p>
            If these Terms are translated and made available in another
            language, the original English text prevails in case of dispute.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 12. Changes to These Terms and Conditions */}
      <section id="changes" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <RefreshCw className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Changes to These Terms and Conditions
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                We may update these terms. For significant changes we will try
                to give at least 30 days' notice.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground">
          <p>
            We may modify or replace these Terms at any time. For material
            changes we will make reasonable efforts to provide at least 30 days'
            notice before they take effect.
          </p>
          <p>
            By continuing to access or use the Service after revisions become
            effective, you agree to be bound by the revised terms. If you do not
            agree, stop using the Service.
          </p>
        </div>
        <div className="ml-14 mt-10 border-b border-border pb-1" />
      </section>

      {/* 13. Contact Us */}
      <section id="contact" className="scroll-mt-24">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 dark:bg-primary-active/10 border border-emerald-600/20 dark:border-primary-active/20 mt-0.5">
            <Mail className="size-5.5 text-emerald-600 dark:text-primary-active" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Contact Us
            </h2>
            <div className="mt-3 rounded-lg border border-emerald-600/20 dark:border-primary-active/20 bg-emerald-600/5 dark:bg-primary-active/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-semibold">In plain English: </span>
                Reach out if you have questions about these terms.
              </p>
            </div>
          </div>
        </div>

        <div className="ml-14 space-y-4 text-sm text-muted-foreground">
          <p>
            If you have any questions about these Terms and Conditions, contact
            us:
          </p>
          <div className="rounded-xl bg-muted border border-border p-6 space-y-3 text-sm mt-4">
            <div className="flex gap-4">
              <span className="w-24 font-medium text-foreground">Email:</span>
              <a
                href="mailto:hq@euclideum.com"
                className="text-emerald-600 dark:text-primary-active hover:underline"
              >
                hq@euclideum.com
              </a>
            </div>
            <div className="flex gap-4">
              <span className="w-24 font-medium text-foreground">Website:</span>
              <a
                href="https://euclideum.com/"
                className="text-emerald-600 dark:text-primary-active hover:underline"
              >
                https://euclideum.com/
              </a>
            </div>
            <div className="flex gap-4">
              <span className="w-24 font-medium text-foreground">Company:</span>
              <span>Euclideum Solutions Pvt. Ltd.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final notice */}
      <div className="rounded-2xl bg-card border border-border px-8 py-8 text-foreground space-y-3 mt-12">
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 dark:text-primary-active">
          Notice
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          These Terms were last updated on {LAST_UPDATED}. They form the entire
          agreement regarding your use of the Service.
        </p>
      </div>
    </>
  )
}
